/**
 * Amendment Tracking Helper
 *
 * Stores amendment history on each benefit's additional_details.amendment_tracking[]
 * Used for waiting period calculations during claims processing.
 *
 * When a member upgrades plans during waiting period, claims pay out on previous plan's benefit.
 */

import { getUserFullname } from '../../../../../../lib/access.es6';

/**
 * Create amendment tracking entry for a benefit
 * @param {Object} oldBenefit - The previous benefit state
 * @param {Object} newBenefit - The new benefit state
 * @param {Object} oldQuotation - The previous quotation object
 * @param {String} newPlan - The new plan code (e.g., "Plan_7")
 * @param {String} amendmentType - Type of amendment ('change_plan', 'change_coverage', 'manage_benefits')
 * @returns {Object} - Tracking entry object
 */
export const createTrackingEntry = (oldBenefit, newBenefit, oldQuotation, newPlan, amendmentType) => {
  // Get premium from various possible locations
  const getOldPremium = () => {
    if (oldBenefit.additional_details && oldBenefit.additional_details.premium_amount !== undefined) {
      return oldBenefit.additional_details.premium_amount;
    }
    return oldBenefit.premium_amount || 0;
  };

  return {
    // Previous state
    previous_cover_amount_in_cents: oldBenefit.cover_amount_in_cents,
    previous_plan: oldQuotation && oldQuotation.additional_details
      ? oldQuotation.additional_details.primary_plan
      : null,
    previous_created_at: oldBenefit.created_at,
    previous_waiting_period_in_days: oldBenefit.waiting_period_in_days,
    previous_premium_amount_in_cents: getOldPremium(),
    previous_quotation_guid: oldQuotation ? oldQuotation.guid : null,

    // New state
    new_plan: newPlan,
    new_cover_amount_in_cents: newBenefit.cover_amount_in_cents,

    // Metadata
    amendment_date: new Date().toISOString(),
    amendment_type: amendmentType,
    amended_by: getUserFullname() || 'Unknown'
  };
};

/**
 * Add tracking to benefit's additional_details
 * Preserves existing tracking history and limits to 6 entries
 *
 * @param {Object} newBenefit - The new benefit object
 * @param {Object} oldBenefit - The old benefit object (for previous state)
 * @param {Object} oldQuotation - The old quotation object
 * @param {String} newPlan - The new plan code
 * @param {String} amendmentType - Type of amendment
 * @returns {Object} - New benefit with amendment_tracking added
 */
export const addAmendmentTracking = (newBenefit, oldBenefit, oldQuotation, newPlan, amendmentType) => {
  // Get existing tracking from old benefit
  const existingTracking = oldBenefit.additional_details && oldBenefit.additional_details.amendment_tracking
    ? oldBenefit.additional_details.amendment_tracking
    : [];

  // If benefit amount didn't change, preserve existing tracking but don't add new entry
  if (oldBenefit.cover_amount_in_cents === newBenefit.cover_amount_in_cents) {
    if (existingTracking.length > 0) {
      return {
        ...newBenefit,
        additional_details: {
          ...newBenefit.additional_details,
          amendment_tracking: existingTracking
        }
      };
    }
    return newBenefit;
  }

  const trackingEntry = createTrackingEntry(oldBenefit, newBenefit, oldQuotation, newPlan, amendmentType);

  // Prepend new entry and limit to 6
  const newTracking = [trackingEntry, ...existingTracking].slice(0, 6);

  return {
    ...newBenefit,
    additional_details: {
      ...newBenefit.additional_details,
      amendment_tracking: newTracking
    }
  };
};

/**
 * Find matching old benefit by type
 * @param {Array} oldBenefits - Array of old benefit objects
 * @param {String} benefitType - Benefit type to find (e.g., "FUNERAL BENEFIT")
 * @returns {Object|null} - Matching benefit or null
 */
export const findMatchingOldBenefit = (oldBenefits, benefitType) => {
  if (!oldBenefits || !Array.isArray(oldBenefits)) return null;
  return oldBenefits.find(b => b.type === benefitType) || null;
};

/**
 * Check if a benefit has amendment tracking history
 * @param {Object} benefit - The benefit object
 * @returns {Boolean}
 */
export const hasAmendmentTracking = (benefit) => {
  return benefit &&
         benefit.additional_details &&
         benefit.additional_details.amendment_tracking &&
         benefit.additional_details.amendment_tracking.length > 0;
};

/**
 * Get the most recent tracking entry for a benefit
 * @param {Object} benefit - The benefit object
 * @returns {Object|null} - Most recent tracking entry or null
 */
export const getLatestTracking = (benefit) => {
  if (!hasAmendmentTracking(benefit)) return null;
  return benefit.additional_details.amendment_tracking[0];
};
